import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { defaultLocale, defaultLocaleText } from '../constants/locale';
import { darkTheme, lightTheme } from '../theme/theme';

interface ThemeContextType {
    isDarkMode: boolean;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
    isDarkMode: false,
    toggleTheme: () => {},
});

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme ? savedTheme === 'dark' : true;
    });

    useEffect(() => {
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const toggleTheme = () => {
        setIsDarkMode((prev) => !prev);
    };

    const baseTheme = isDarkMode ? darkTheme : lightTheme;
    const theme = createTheme(baseTheme);

    return (
        <LocalizationProvider //Localization object needed by MUI Theme
            dateAdapter={AdapterDateFns}
            adapterLocale={defaultLocale}
            localeText={defaultLocaleText.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
                <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
            </ThemeContext.Provider>
        </LocalizationProvider>
    );
};
